/**
 * PHX_LOCALE_PROVIDER
 *
 * Injection token that allows the host application to supply locale-aware
 * formatting functions and configuration to Phoenix components, without
 * Phoenix depending on mt-shared.
 *
 * Phoenix declares the *contract* (what functions it needs).
 * mt-shared implements them.  The app layer wires the two together.
 *
 * @example
 * // In app.module.ts or root providers:
 * {
 *   provide: PHX_LOCALE_PROVIDER,
 *   useFactory: (svc: LocalizationService) => ({
 *     formatDate: (d, fmt) => svc.formatDate(d, { shortFormat: fmt === PhxDateFormatStyle.DEFAULT }),
 *     formatTime: (d) => svc.formatTime(d),
 *     formatNumber: (n, opts) => svc.formatNumber(n, opts),
 *     getDateFnsLocale: () => svc.getDateFnsLocale(),
 *     getFirstDayOfWeek: () => svc.getFirstDayOfWeek(),
 *     uses24HourClock: () => svc.uses24HourClock(),
 *     locale$: svc.currentLocale$.pipe(map(r => r.locale)),
 *   }),
 *   deps: [LocalizationService],
 * }
 */

import { InjectionToken } from '@angular/core';
import { Locale } from 'date-fns';
import { Observable } from 'rxjs';

/**
 * Contract that Phoenix components use for all locale-aware operations.
 * Implemented by the host app using mt-shared's formatters.
 */
export enum PhxDateFormatStyle {
  /** Default locale format (e.g. "Jan 15, 2026" for US, "15. Jan. 2026" for DE) */
  DEFAULT = 'default',
  /** Locale format without year (e.g. "Jan 15", "15. Jan.") */
  WITHOUT_YEAR = 'without-year',
  /** Locale date and time with local timezone via Intl (e.g. "Apr 12, 2026, 3:33 PM PT" or "GMT+5"). */
  WITH_TIME_ZONE = 'with-time-zone',
}

export interface PhxDateFormatOptions {
  /** When true, format in the browser's local timezone instead of the account timezone. */
  skipTimezone?: boolean;
}

export interface PhxElapsedFormatOptions {
  /** Text appended to the relative duration string (e.g. "ago"). */
  suffix?: string;
  /** When true, shows only the single most significant unit (e.g. "24m ago"). */
  singleUnit?: boolean;
}

export interface PhxLocaleProvider {
  // ---- Formatting functions ----

  /** Format a date value into a locale-correct string. */
  formatDate(date: Date | string | number, formatStr: PhxDateFormatStyle, options?: PhxDateFormatOptions): string;

  /** Format a time value into a locale-correct string */
  formatTime(date: Date | string | number): string;

  /** Format a compact elapsed-time label (e.g. "24m ago"). */
  formatElapsed(date: Date | string | number, options?: PhxElapsedFormatOptions): string;

  /** Format a number into a locale-correct string */
  formatNumber(value: number, precision?: number): string;

  /** Return the locale-aware Intl parts for a date value */
  formatDateToParts(date: Date | string | number): Intl.DateTimeFormatPart[];

  /** Format a number into a compact, locale-correct string */
  formatCompactNumber(value: number): string;

  /** Format a number into a percentage string */
  formatPercentage(value: number, precision?: number): string;

  // ---- Locale accessors (for calendar grid, date-fns calls) ----

  /** Get the current date-fns Locale object */
  getDateFnsLocale(): Locale;

  /** Get first day of week: 0 = Sunday, 1 = Monday */
  getFirstDayOfWeek(): number;

  /**
   * Whether the app's time formatting uses a 24-hour clock (no AM/PM) for
   * time-only display, matching {@link formatTime}. Used to align pickers with locale.
   */
  uses24HourClock(): boolean;

  /**
   * Get a locale-aware abbreviated month name (e.g. "Jan", "Jän.", "janv.").
   * @param date - Date object from which the month is extracted
   */
  formatMonth(date: Date): string;

  /**
   * Get all 12 abbreviated month names for the current locale.
   * Returns a 0-indexed array: index 0 = January, index 11 = December.
   */
  getAllMonthsOfYearArray(): string[];

  /** Observable that emits the BCP-47 locale tag whenever it changes */
  locale$: Observable<string>;
}

/**
 * DI token for the locale provider.
 * Phoenix injects this with @Optional() — when not provided,
 * Phoenix falls back to its existing English-only behaviour.
 */
export const PHX_LOCALE_PROVIDER = new InjectionToken<PhxLocaleProvider>(
  'PHX_LOCALE_PROVIDER'
);
